import { Id } from '@/convex/_generated/dataModel';
import { useActiveContext } from './useActiveContext';

interface UseCurrentContextOptions {
  throwOnError?: boolean;
}

interface UseCurrentContextReturn {
  contextId: Id<'contexts'> | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to get the current active context ID
 * This hook wraps useActiveContext and provides just the context ID
 * to avoid passing contextId to every hook call
 * 
 * @param options - Configuration options
 * @param options.throwOnError - If true, throws an error when no context is available (default: false)
 * @returns Object containing contextId, loading state, and error
 * @throws Error if throwOnError is true and no context is available
 * 
 * @example
 * // Basic usage
 * const { contextId, isLoading } = useCurrentContext();
 * 
 * @example
 * // With error throwing for required contexts
 * const { contextId } = useCurrentContext({ throwOnError: true });
 * // contextId is guaranteed to be non-null here
 */
export function useCurrentContext(options: UseCurrentContextOptions = {}): UseCurrentContextReturn {
  const { throwOnError = false } = options;
  const { activeContext, isLoading } = useActiveContext();

  // Loading state
  if (isLoading || activeContext === undefined) {
    return {
      contextId: null,
      isLoading: true,
      error: null,
    };
  }

  // No context available
  if (!activeContext) {
    const error = 'No active context';
    
    if (throwOnError) {
      throw new Error('No active context available');
    }
    
    return {
      contextId: null,
      isLoading: false,
      error,
    };
  }

  // Context available
  return {
    contextId: activeContext._id,
    isLoading: false,
    error: null,
  };
}

